import { CircularProgress } from '@material-ui/core';
import type { ProjectFileData } from '@mogul/api-client';
import { ProjectInclude, ProjectProductionStatus } from '@mogul/api-client';
import { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router';
import { useParams } from 'react-router-dom';
import * as r from 'runtypes';

import { projectsApi } from '../../../api';
import { BackHeader } from '../../../components/common/backheader/BackHeader.component';
import { Button } from '../../../components/core/button';
import type { CastAndCrewData } from '../../../components/movie/card/AdditionalInfo.component';
import { FinancedProject } from '../../../components/movie/FinancedProject.component';
import { defaultYoutubeLink } from '../../../constants/links';
import { useNetwork, useSwitchChain } from '../../../hooks';
import { WEB3_CONFIG } from '../../../lib';
import styles from './FinancedProjectPage.module.css';
import type { ProjectMetadata } from './ProjectPage.container';

const INITIAL_MOVIE_META: ProjectMetadata = {
  description: '',
  genre: '',
  image: '',
  name: '',
  studio: '',
};

export const FinancedProjectPage: React.FC = () => {
  const history = useHistory();
  const { projectId } = r.Record({ projectId: r.String }).check(useParams());
  const { switchNetworkAsync } = useSwitchChain();
  const { isUnsupported } = useNetwork();

  const [movieMetadata, setMovieMetadata] = useState(INITIAL_MOVIE_META);
  const [trailerUrl, setTrailerUrl] = useState('');
  const [cast, setCast] = useState<CastAndCrewData[]>([]);
  const [budget, setBudget] = useState('');
  const [additionalDetails, setAdditionalDetails] = useState<ProjectFileData[]>(
    [],
  );
  const [productionStatus, setProductionStatus] =
    useState<ProjectProductionStatus>(ProjectProductionStatus.PreProduction);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    void (async () => {
      const projectResponse = await projectsApi.getProject({
        id: projectId,
        include: [ProjectInclude.AttachedFiles],
      });
      const projectData = projectResponse.data.data;
      const movieData = {
        description: projectData.description,
        genre: projectData.genre ?? '',
        image: projectData.posterImage?.publicUrl ?? '',
        name: projectData.title,
        studio: projectData.studio ?? '',
      };

      const movieFileAttachmentsData = projectData.attachedFiles ?? [];
      setAdditionalDetails(movieFileAttachmentsData);

      const crewData = projectData.crew as CastAndCrewData[];
      const castData = projectData.cast as CastAndCrewData[];
      const castAndCrewData: CastAndCrewData[] = crewData.concat(
        castData.map(castObj => ({ ...castObj, role: 'Actor' })),
      );
      setMovieMetadata(movieData);
      setCast(castAndCrewData);
      setProductionStatus(projectData.productionStatus);
      setTrailerUrl(projectData.trailerUrl ?? defaultYoutubeLink);
      setBudget(projectData.budget?.toLocaleString() ?? '0');
      setLoading(false);
    })();
  }, []);

  const handleBnbSwitch = useCallback(
    () => switchNetworkAsync?.(WEB3_CONFIG.chains.bsc.id),
    [switchNetworkAsync],
  );

  if (isUnsupported && !loading) {
    return (
      <div className={styles.movieVotingPage}>
        <div className={styles.movieVotingContainer + ' max-w-screen-lg mt-10'}>
          <div className="flex flex-col items-center justify-center w-full h-full">
            <div className="flex flex-col items-center justify-center h-full max-w-screen-lg w-4/5">
              <p className="text-lg">You are using the wrong network ...</p>
              <Button className="mt-8" onClick={handleBnbSwitch}>
                Switch To BNB Chain
              </Button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={
        styles.financedMoviePageContainer +
        ' flex flex-col items-center justify-center'
      }
    >
      {!loading && (
        <div className="w-full ml-16">
          <BackHeader
            title="back to Voting"
            headerOnPress={() => history.push('/platform/voting')}
          />
        </div>
      )}
      {loading ? (
        <CircularProgress
          className={'grid place-items-center w-full h-full text-white'}
          style={{ color: 'white' }}
          size={100}
        />
      ) : (
        <FinancedProject
          {...movieMetadata}
          productionStatus={productionStatus}
          description={movieMetadata.description}
          trailerLink={trailerUrl}
          budget={budget}
          cast={cast}
          additionalDetails={additionalDetails}
        />
      )}
    </div>
  );
};
